import { AllocationsInPeriod, ProjectAllocationBase, ProjectPercents } from '@neo.tax/shared/types/';

export const getProjectAllocationsForProjectPercents = <T extends ProjectAllocationBase>(
  projectPercents: ProjectPercents<T>,
): [string, T][] => {
  const { projectIdToProjectAllocations, selectedIdToProjectAllocations } = projectPercents;
  return [...Array.from(selectedIdToProjectAllocations), ...Array.from(projectIdToProjectAllocations)];
};

export const getTotalAllocationPercent = <
  T extends ProjectAllocationBase,
  K extends AllocationsInPeriod<T> = AllocationsInPeriod<T>,
>(
  allocationsInPeriod: K,
): number => {
  const { nonProjectPercent, projectPercents } = allocationsInPeriod;
  const projectAllocations = getProjectAllocationsForProjectPercents(projectPercents);
  const total = projectAllocations.reduce(
    (acc, [_, value]) => acc + value.allocationPercent,
    nonProjectPercent.allocationPercent,
  );
  return total;
};

/**
 * Checks if any allocation percentages have been modified from their original values.
 * Compares both project allocations and non-project percentage against their original values.
 *
 * @param allocationsInPeriod - The current allocation state to check
 * @returns true if any allocations have been modified, false otherwise
 */
export const allocationsHaveChanges = <
  T extends ProjectAllocationBase,
  K extends AllocationsInPeriod<T> = AllocationsInPeriod<T>,
>(
  allocationsInPeriod: K,
): boolean => {
  const {
    projectPercents: { projectIdToProjectAllocations },
  } = allocationsInPeriod;

  // Check if any project allocations have changed
  for (const projectAllocation of projectIdToProjectAllocations.values()) {
    if (projectAllocation.allocationPercent !== projectAllocation.originalAllocationPercent) {
      return true;
    }
  }

  // Check if non-project percentage has changed
  return (
    allocationsInPeriod.nonProjectPercent.allocationPercent !==
    allocationsInPeriod.nonProjectPercent.originalAllocationPercent
  );
};

/**
 * Calculates new project allocations proportionally based on a change in non-project time.
 *
 * When non-project time increases/decreases, this reduces/increases project allocations
 * proportionally to maintain a 100% total. Uses the last edited values as the basis for
 * proportional distribution to preserve user intent.
 *
 * Example:
 * - Non-project: 10% → 20% (delta = +10%)
 * - Project A was 60%, Project B was 30%
 * - Project A gets reduced by 10% × (60/90) = 6.67% → ~54%
 * - Project B gets reduced by 10% × (30/90) = 3.33% → ~27%
 *
 * @param delta - How much non-project time changed (positive = increased, negative = decreased)
 * @param projectPercents - Current project allocation maps
 * @param totalLastAllocationPercent - Sum of last edited project percentages
 * @param totalCurrentAllocationPercent - Sum of current project percentages
 * @returns New project allocation maps with proportionally adjusted percentages
 */
const calculateProportionalProjectAdjustments = <T extends ProjectAllocationBase>(
  delta: number,
  projectIdToProjectAllocations: Map<string, T>,
  selectedIdToProjectAllocations: Map<string, T>,
  totalLastAllocationPercentOnProjectPercentUpdate: number,
  totalProjectAllocationPercent: number,
): [Map<string, T>, Map<string, T>] =>
  [projectIdToProjectAllocations, selectedIdToProjectAllocations].map(
    (projectPercents) =>
      new Map(
        Array.from(projectPercents).map<[string, T]>(([key, value]) => {
          // If no projects have ever been edited (all lastAllocationPercent = 0),
          // use current allocations instead to avoid division by zero when calculating proportions.
          // If current allocations are also 0, we handle that case separately below.
          const baseForCalculation =
            totalLastAllocationPercentOnProjectPercentUpdate === 0
              ? totalProjectAllocationPercent
              : totalLastAllocationPercentOnProjectPercentUpdate;
          const valueForCalculation =
            totalLastAllocationPercentOnProjectPercentUpdate === 0
              ? value.allocationPercent
              : value.lastAllocationPercentOnProjectPercentUpdate;

          if (baseForCalculation === 0) {
            // Both last and current allocations are 0, so keep at 0
            return [
              key,
              {
                ...value,
                allocationPercent: 0,
              },
            ];
          }

          // Adjust the project percents proportionally based on the last edited project percents
          const targetReduction = delta * (valueForCalculation / baseForCalculation);
          const newAllocationPercent = Math.max(0, Math.min(Math.round(valueForCalculation - targetReduction), 100));
          return [
            key,
            {
              ...value,
              allocationPercent: newAllocationPercent,
            },
          ];
        }),
      ),
  ) as [Map<string, T>, Map<string, T>];

/**
 * Redistributes excess allocation when total exceeds 100%.
 *
 * This happens when proportional adjustments cause some projects to hit 0% floor,
 * meaning they couldn't absorb their full share of the reduction. The excess gets
 * redistributed across remaining non-zero projects proportionally.
 *
 * Example scenario:
 * - Need to reduce projects by 15% total
 * - Project A (5%) can only absorb 5%, leaving 10% unabsorbed
 * - Project B (50%) and C (40%) split the remaining 10% proportionally
 *
 * May require multiple iterations if projects keep hitting 0%.
 *
 * @param projectPercents - Project allocation maps (mutated in place)
 * @param nonProjectPercent - New non-project percentage
 * @returns New total after redistribution
 */
const redistributeExcessAllocation = <T extends ProjectAllocationBase>(
  newProjectPercents: Map<string, T>,
  newSelectedProjectPercent: Map<string, T>,
  newAllocationPercent: number,
): number => {
  let newTotal = getProjectAllocationsForProjectPercents({
    projectIdToProjectAllocations: newProjectPercents,
    selectedIdToProjectAllocations: newSelectedProjectPercent,
  }).reduce((acc, [_, value]) => acc + Math.round(value.allocationPercent), newAllocationPercent);

  let iterationCount = 0;
  const MAX_ITERATIONS = 10; // Safety limit

  while (newTotal > 100 && iterationCount < MAX_ITERATIONS) {
    iterationCount++;
    const excess = newTotal - 100;

    // Collect all projects with non-zero allocations
    const remainingProjects: Array<{
      key: string;
      value: T;
      map: Map<string, T>;
    }> = [];

    Array.from(newSelectedProjectPercent.entries()).forEach(([key, value]) => {
      if (value.allocationPercent > 0) {
        remainingProjects.push({ key, value, map: newSelectedProjectPercent });
      }
    });

    Array.from(newProjectPercents.entries()).forEach(([key, value]) => {
      if (value.allocationPercent > 0) {
        remainingProjects.push({ key, value, map: newProjectPercents });
      }
    });

    if (remainingProjects.length === 0) {
      break;
    }

    const totalRemainingAllocation = remainingProjects.reduce((sum, p) => sum + p.value.allocationPercent, 0);

    // Distribute the excess proportionally across remaining projects
    let distributedSoFar = 0;
    remainingProjects.forEach((project, index) => {
      const isLastProject = index === remainingProjects.length - 1;
      let reduction: number;

      if (isLastProject) {
        // Give the last project all remaining to avoid rounding issues
        reduction = excess - distributedSoFar;
      } else {
        // Distribute proportionally
        reduction = Math.round((project.value.allocationPercent / totalRemainingAllocation) * excess);
        distributedSoFar += reduction;
      }

      const newAllocation = Math.max(0, project.value.allocationPercent - reduction);

      const updatedProject = {
        ...project.value,
        allocationPercent: newAllocation,
      };

      project.map.set(project.key, updatedProject);
    });

    // Recalculate total
    newTotal = getProjectAllocationsForProjectPercents({
      projectIdToProjectAllocations: newProjectPercents,
      selectedIdToProjectAllocations: newSelectedProjectPercent,
    }).reduce((acc, [_, value]) => acc + Math.round(value.allocationPercent), newAllocationPercent);
  }

  return newTotal;
};

/**
 * Adds remaining allocation to the most allocated project when total is under 100%.
 *
 * This handles rounding discrepancies where we're a few percentage points short of 100%.
 * Rather than distribute tiny amounts across many projects, we add it all to the largest
 * project for simplicity.
 *
 * Example:
 * - Total is 98% after rounding adjustments
 * - Project A has 60%, Project B has 38%
 * - Add the 2% diff to Project A (largest) → A becomes 62%
 *
 * @param projectPercents - Project allocation maps (mutated in place)
 * @param diff - How much we're under 100%
 */
const addRemainingToMostAllocatedProject = <T extends ProjectAllocationBase>(
  newProjectPercents: Map<string, T>,
  newSelectedProjectPercent: Map<string, T>,
  diff: number,
): void => {
  let mostAllocatedProject: T | undefined;

  // Find the most allocated project
  [newProjectPercents, newSelectedProjectPercent].forEach((projectPercents) => {
    projectPercents.forEach((value) => {
      const allocationPercent = value.allocationPercent;
      if (allocationPercent === 0) {
        return;
      }
      if ((mostAllocatedProject?.allocationPercent ?? 0) < allocationPercent) {
        mostAllocatedProject = value;
      }
    });
  });

  // If the most allocated project is found, add the diff to it
  if (mostAllocatedProject) {
    const newMostAllocatedProject = {
      ...mostAllocatedProject,
      allocationPercent: Math.max(mostAllocatedProject.allocationPercent + diff, 0),
    };

    [newProjectPercents, newSelectedProjectPercent].forEach((projectPercents) => {
      if (projectPercents.has(newMostAllocatedProject.projectId)) {
        projectPercents.set(newMostAllocatedProject.projectId, newMostAllocatedProject);
      }
    });
  }
};

/**
 * Updates project allocations when non-project time percentage changes.
 *
 * This is the main coordinator function that:
 * 1. Checks if we should adjust projects at all (only if total was/is 100%)
 * 2. Calculates proportional adjustments to projects
 * 3. Redistributes any excess if some projects hit 0% floor
 * 4. Adds any deficit to the largest project if we're under 100%
 *
 * The goal is to maintain 100% total while respecting:
 * - Projects can't go below 0% or above 100%
 * - Adjustments should be proportional to preserve user intent
 * - Rounding should not cause persistent drift
 *
 * @param allocationsInPeriod - Current allocation state
 * @param newAllocationPercent - New non-project percentage
 * @returns Updated allocation state with adjusted project percentages
 */
export const updateNonProjectTimePercent = <
  T extends ProjectAllocationBase,
  K extends AllocationsInPeriod<T> = AllocationsInPeriod<T>,
>({
  allocationsInPeriod,
  newAllocationPercent,
}: {
  allocationsInPeriod: K;
  newAllocationPercent: number;
}): K => {
  const { nonProjectPercent, projectPercents } = allocationsInPeriod;
  const { projectIdToProjectAllocations, selectedIdToProjectAllocations } = projectPercents;
  const totalLastAllocationPercentOnProjectPercentUpdate = getProjectAllocationsForProjectPercents(
    projectPercents,
  ).reduce((acc, [_, value]) => acc + Math.round(value.lastAllocationPercentOnProjectPercentUpdate), 0);
  const totalProjectAllocationPercent = getProjectAllocationsForProjectPercents(projectPercents).reduce(
    (acc, [_, value]) => acc + Math.round(value.allocationPercent),
    0,
  );

  const totalAllocationPercent = totalProjectAllocationPercent + nonProjectPercent.allocationPercent;
  const newTotalAllocationPercent = totalProjectAllocationPercent + newAllocationPercent;

  // Only adjust projects if we're crossing or maintaining the 100% threshold
  // If we're staying under or staying over 100%, just update non-project time without adjusting projects
  if (
    (newTotalAllocationPercent < 100 && totalAllocationPercent < 100) ||
    (newTotalAllocationPercent > 100 && totalAllocationPercent > 100)
  ) {
    return {
      ...allocationsInPeriod,
      projectPercents,
      nonProjectPercent: {
        ...nonProjectPercent,
        allocationPercent: newAllocationPercent,
      },
    };
  }

  const delta = newAllocationPercent - nonProjectPercent.lastAllocationPercentOnProjectPercentUpdate;

  const [newProjectPercents, newSelectedProjectPercent] = calculateProportionalProjectAdjustments(
    delta,
    projectIdToProjectAllocations,
    selectedIdToProjectAllocations,
    totalLastAllocationPercentOnProjectPercentUpdate,
    totalProjectAllocationPercent,
  );

  let newTotal = getProjectAllocationsForProjectPercents({
    projectIdToProjectAllocations: newProjectPercents,
    selectedIdToProjectAllocations: newSelectedProjectPercent,
  }).reduce((acc, [_, value]) => acc + Math.round(value.allocationPercent), newAllocationPercent);

  // If we're exactly at 100%, we're done
  if (newTotal === 100) {
    return {
      ...allocationsInPeriod,
      projectPercents: {
        projectIdToProjectAllocations: newProjectPercents,
        selectedIdToProjectAllocations: newSelectedProjectPercent,
      },
      nonProjectPercent: {
        ...nonProjectPercent,
        allocationPercent: newAllocationPercent,
      },
    };
  }

  // If over 100%, redistribute excess across remaining projects
  if (newTotal > 100) {
    newTotal = redistributeExcessAllocation(newProjectPercents, newSelectedProjectPercent, newAllocationPercent);
  }

  // If under 100%, add the diff to the most allocated project
  if (newTotal < 100) {
    const diff = 100 - newTotal;
    addRemainingToMostAllocatedProject(newProjectPercents, newSelectedProjectPercent, diff);
  }

  return {
    ...allocationsInPeriod,
    projectPercents: {
      projectIdToProjectAllocations: newProjectPercents,
      selectedIdToProjectAllocations: newSelectedProjectPercent,
    },
    nonProjectPercent: {
      ...nonProjectPercent,
      allocationPercent: newAllocationPercent,
    },
  };
};

export const updateProjectAllocationPercent = <
  T extends ProjectAllocationBase,
  K extends AllocationsInPeriod<T> = AllocationsInPeriod<T>,
>({
  allocationsInPeriod,
  newAllocationPercent,
  projectId,
}: {
  allocationsInPeriod: K;
  newAllocationPercent: number;
  projectId: string;
}): K => {
  const { nonProjectPercent, projectPercents } = allocationsInPeriod;
  const { projectIdToProjectAllocations, selectedIdToProjectAllocations } = projectPercents;
  const totalAllocationPercent = getProjectAllocationsForProjectPercents(projectPercents).reduce(
    (acc, [_, value]) => acc + Math.round(value.allocationPercent),
    nonProjectPercent.allocationPercent,
  );
  let newNonProjectPercent = nonProjectPercent;

  const [newSelectedProjectPercent, newProjectPercents] = [
    selectedIdToProjectAllocations,
    projectIdToProjectAllocations,
  ].map(
    (oldProjectPercents) =>
      new Map(
        Array.from(oldProjectPercents).map(([key, value]) => {
          if (key !== projectId) {
            // If the project is not the one being updated,
            // just update lastAllocationPercentOnProjectPercentUpdate
            return [key, { ...value, lastAllocationPercentOnProjectPercentUpdate: value.allocationPercent }];
          }
          let allocationPercent = Math.round(value.allocationPercent);
          const diff = allocationPercent - newAllocationPercent;
          allocationPercent = newAllocationPercent;
          if (totalAllocationPercent === 100) {
            newNonProjectPercent = {
              ...nonProjectPercent,
              allocationPercent: Math.max(0, nonProjectPercent.allocationPercent + diff),
            };
          }
          return [
            key,
            {
              ...value,
              allocationPercent,
              lastAllocationPercentOnProjectPercentUpdate: allocationPercent,
            },
          ];
        }),
      ),
  );

  const newProjectPercentsMap = {
    projectIdToProjectAllocations: newProjectPercents,
    selectedIdToProjectAllocations: newSelectedProjectPercent,
  };
  const newTotalProjectAllocationPercent = getProjectAllocationsForProjectPercents(newProjectPercentsMap).reduce(
    (acc, [_, value]) => acc + Math.round(value.allocationPercent),
    0,
  );
  const newTotalAllocationPercent = newTotalProjectAllocationPercent + newNonProjectPercent.allocationPercent;

  if (
    (newTotalAllocationPercent < 100 && totalAllocationPercent > 100) ||
    (newTotalAllocationPercent > 100 && totalAllocationPercent < 100)
  ) {
    // Update from a slider doesn't necessarily come incrementally,
    // so we need to adjust the non-project percent in case the value jumps
    newNonProjectPercent = {
      ...newNonProjectPercent,
      allocationPercent: Math.max(0, 100 - newTotalProjectAllocationPercent),
    };
  }

  return {
    ...allocationsInPeriod,
    projectPercents: newProjectPercentsMap,
    nonProjectPercent: {
      ...newNonProjectPercent,
      allocationPercent: Math.round(newNonProjectPercent.allocationPercent),
      lastAllocationPercentOnProjectPercentUpdate: Math.round(newNonProjectPercent.allocationPercent),
    },
  };
};
